import { QueryObserverOptions, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import {
  ChronosphereDataQueryDefinition,
  FormulaDefinition,
  QueryDefinition,
  QueryThreshold,
  SupportedDataType,
} from '@/core';
import { GetDataResponse, InternalGetDataResponse } from '../../model';
import { DataQueryMode, PaginationConfig, UseDataQueryOptions } from '../data-querying';

type DataQueryOptions = Omit<UseDataQueryOptions, 'pagination'>;

export type DataQueryFunction = Required<Pick<UseQueryOptions<GetDataResponse>, 'queryKey'>> &
  Omit<UseQueryOptions<GetDataResponse>, 'queryKey'>;

export interface DataQueriesProviderProps {
  queries: Array<DataQueryConfig<ChronosphereDataQueryDefinition> | DataQueryFunction>;
  formulas?: FormulaDefinition[];
  options?: DataQueryOptions;
  children?: React.ReactNode;
  queryOptions?: QueryObserverOptions;
  /*
   * Unique identifier that gets included in the queryKey in case multiple instances of the same query need to be treated as separate data sets.
   * Example: multiple panels on a dashboard need to control query limits independently even if they share the same query.
   *
   * If an id is not specified, a unique one will be automatically set for you.
   */
  id?: string;
  /**
   * Make transformations to the queryResult property after the query has been fetched.
   * @experimental This feature may not support more complex viz framework features such as pagination, progressive loading etc. at this time.
   */
  __experimentalTransformQueryResults?: (
    results: Array<QueryData<SupportedDataType>>
  ) => Array<QueryData<SupportedDataType>>;
}
export interface DataQueryConfig<PluginDefinition = ChronosphereDataQueryDefinition> {
  definition: PluginDefinition;
  alias?: string;
  disabled?: boolean;
  id?: string;
  threshold?: QueryThreshold;
}

type Unpacked<T> = T extends Array<infer U> ? U : T; // if T is an array, return the array type, else return T
export interface UseDataQueryResult<T extends SupportedDataType | SupportedDataType[] = SupportedDataType[]> {
  queryResults: Array<QueryData<Unpacked<T>>>;
  /**
   * A unique key for the loaded data. This is used to determine if the data has changed.
   */
  queryKey: string;
  refetchAll: () => void;
  /**
   * If true, the query is currently fetching.
   */
  isFetching: boolean;
  /**
   * If true, the query is currently fetching additional data progressively.
   */
  isFetchingMore: boolean;
  /**
   * If true, the query is still loading for the first time. Disabled queries are not considered loading.
   */
  isLoading: boolean;
  errors: unknown[];
  pagination?: Pagination;
  /**
   * The query mode currently being used for the data queries. This impacts the behavior of the query lifecycle (e.g.
   * fetching data, caching results, refetching when the time range changes). This value will match the passed in
   * `queryMode` if set. Otherwise, it will be the default of `active`.
   */
  queryMode: DataQueryMode;
  /**
   * Unique identifier for the data queries. It is included in the query key for the queries. This value will match the
   * passed in `id` if set. Otherwise, it will return an automatically generated unique id.
   */
  id: string;

  /**
   *  Function that will cancel in progress data queries.
   */
  cancelInProgressDataQueries: () => Promise<void>;

  /**
   * Total series and datapoints statistics. Only included when includeStats: true.
   */
  stats?: {
    totalSeries: number;
    totalDatapoints: number;
    isLoading: boolean;
  };

  /**
   * Common labels across all series. Only included when includeCommonLabels: true.
   */
  commonLabels?: Record<string, string>;
}

export type Pagination = PaginationConfig & {
  totalRowCount: number;
  setPagination: (pagination: { page: number; pageSize: number }) => void;
};

export type QueryData<T extends SupportedDataType = SupportedDataType> = GetDataResponse<T> & {
  definition?: QueryDefinition;
  error: unknown;
  isFetching: boolean;
  isFetchingMore: boolean;
  isLoading: boolean;
  refetch?: () => void;
};

export function transformQueryResults(
  results: Array<UseQueryResult<InternalGetDataResponse>>,
  definitions?: QueryDefinition[]
): QueryData[] {
  return results.map(({ data, isFetching, refetch, error, isInitialLoading }, i) => {
    // Extract custom properties we use for progressive loading state from the
    // rest of the data.
    const { internalMetadata, ...otherData } = data || {};
    const { isFetchingMore, additionalError } = internalMetadata || {};

    // Just casting the data instead of the whole response to minimize the risks of type casting.
    const castedOtherData = otherData as GetDataResponse;

    return {
      ...castedOtherData,
      definition: definitions ? definitions[i] : undefined,
      isFetching,
      isFetchingMore: !!isFetchingMore,

      /**
       * There is an issue where isLoading is true even when a query is disabled in v4. https://github.com/TanStack/query/issues/3584
       * We can use isInitialLoading to determine if the query is still loading for the first time.
       * See: https://github.com/TanStack/query/issues/3584
       */
      isLoading: isInitialLoading,

      refetch,

      // We use `additionalError` to track the latest error for progressive queries because react-query
      // does not natively support this feature and does not give us a safe way to change the top-level `error`
      // property.
      error: additionalError ?? error,
    };
  });
}

export function mergeTimeSeries(queryResults: UseDataQueryResult<'TimeSeries'>['queryResults']) {
  return queryResults
    ?.map((r) => {
      return r.data?.series || [];
    })
    .flat();
}
